import { tt } from '@agos/shared'

/**
 * `/hikvision` — the attendance terminal, over ISAPI.
 *
 * **FB-155.** *Polling interval (sekundy)*, range 10–3 600, help text *"Ako často server stiahne nové
 * záznamy zo zariadenia"* — and the server polls every **60 seconds** whatever it says.
 * `ScheduledJobs.hikvisionPolling()` is `@Scheduled(fixedDelay = 60_000)` and `HikvisionService.sync()` reads
 * `last_sync_at` to decide *what* to fetch, never `poll_interval` to decide *when*. The only other reader of
 * the column is the Control Tower's read-out. Set it to an hour and the terminal is still polled every
 * minute; the page's own footer prints the stored number as though it were the cadence in force.
 *
 * **FB-156.** The webhook URL the page tells you to paste into the device is wrong twice:
 *
 * ```tsx
 * const webhookUrl = `${window.location.origin.replace(/:\d+$/, "")}/api/hikvision/webhook`;
 * ```
 *
 * The path is `/api/hikvision/webhook`; AGOS serves `/api/v1/hikvision/webhook`. And the host comes from the
 * **manager's** origin with its port stripped by a regular expression, which lands on the API only when the
 * API happens to be on port 80 or 443 of the same host — not on a preview where the manager is on 5273. A
 * terminal configured from that string posts its events into nothing.
 *
 * **And two credentials on one form, one hidden and one not.** The device password is correctly never
 * returned — `HikvisionService` says so in its own docblock — while `device_token`, the shared secret the
 * webhook is authenticated with, *is* returned and legacy renders it in a plain text input.
 *
 * On the restored data `hikvision_config` holds **no row**: the terminal has never been set up, and the form's
 * `admin` / `80` / `60` are suggestions rather than stored values.
 */

export interface HikvisionConfig {
  id: number
  ip: string
  port: number
  username: string
  deviceToken: string | null
  pollInterval: number
  lastSyncAt: string | null
  lastSyncCount: number
}

export interface HikvisionForm {
  ip: string
  port: string
  username: string
  /** Empty means *leave the stored password alone*. Never populated from the server. */
  password: string
  /** Empty means *leave the stored token alone*, for the same reason. */
  deviceToken: string
  pollInterval: string
}

export const DEFAULT_PORT = 80
export const DEFAULT_POLL_SECONDS = 60
export const MIN_POLL_SECONDS = 10
export const MAX_POLL_SECONDS = 3600

/** What the job really does, regardless of `poll_interval`. */
export const ACTUAL_POLL_SECONDS = 60

/** Suggestions, not values — the form starts empty when nothing is stored. */
export const SUGGESTED = { port: String(DEFAULT_PORT), username: 'admin', pollInterval: '60' } as const

export function configOf(payload: unknown): HikvisionConfig | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  if (typeof row.ip !== 'string' || row.ip.trim() === '') return null
  return {
    id: typeof row.id === 'number' ? row.id : 0,
    ip: row.ip,
    port: typeof row.port === 'number' ? row.port : DEFAULT_PORT,
    username: typeof row.username === 'string' ? row.username : '',
    deviceToken: typeof row.deviceToken === 'string' && row.deviceToken !== '' ? row.deviceToken : null,
    pollInterval: typeof row.pollInterval === 'number' ? row.pollInterval : DEFAULT_POLL_SECONDS,
    lastSyncAt:
      typeof row.lastSyncAt === 'string' && row.lastSyncAt.trim() !== '' ? row.lastSyncAt : null,
    lastSyncCount: typeof row.lastSyncCount === 'number' ? row.lastSyncCount : 0,
  }
}

export function formOf(config: HikvisionConfig | null): HikvisionForm {
  if (config === null) {
    // Nothing stored: the boxes are empty and the suggestions are offered, not filled in.
    return { ip: '', port: '', username: '', password: '', deviceToken: '', pollInterval: '' }
  }
  return {
    ip: config.ip,
    port: String(config.port),
    username: config.username,
    password: '',
    deviceToken: '',
    pollInterval: String(config.pollInterval),
  }
}

export function carriesToken(config: HikvisionConfig | null): boolean {
  return config?.deviceToken !== null && config?.deviceToken !== undefined
}

// ─── What is actually stored, and what is a suggestion ───────────────────────

export type SetupState = 'not-configured' | 'configured'

export function setupState(config: HikvisionConfig | null): SetupState {
  return config === null ? 'not-configured' : 'configured'
}

export function setupMessage(state: SetupState): string {
  if (state === 'not-configured') {
    return tt('Terminál nie je nastavený - nič sa z neho nesťahuje a webhook nemá kam posielať.')
  }
  return tt('Terminál je nastavený. Server z neho sťahuje záznamy a prijíma push udalosti.')
}

export function lastSyncLabel(config: HikvisionConfig | null): string {
  if (config === null || config.lastSyncAt === null) return tt('Nikdy')
  return config.lastSyncAt
}

// ─── FB-155: the interval, which is honoured now ─────────────────────────────

/**
 * What the cadence actually is.
 *
 * The job runs every 60 seconds and **each franchise skips the turns its own interval does not want**,
 * so the stored figure is the floor rather than the schedule. It used to be read by nothing at all,
 * and this note said so; what it says now is the one thing a reader still needs, which is that a value
 * below the job's own minute cannot buy anything.
 */
export function pollIntervalNote(form: HikvisionForm): string {
  const stored = Number(form.pollInterval.trim())
  if (!Number.isFinite(stored) || stored <= 0) {
    return tt('Server sa pozrie na terminál každých 60 sekúnd.')
  }
  if (stored <= ACTUAL_POLL_SECONDS) {
    return `${tt('Úloha beží každých')} ${ACTUAL_POLL_SECONDS} ${tt('s, takže častejšie ako raz za minútu sa sťahovať nedá - nižšia hodnota nič nezrýchli.')}`
  }
  return `${tt('Server sa pozrie na terminál každých')} ${stored} ${tt('s. Úloha sa budí každú minútu a medzitým turnusy preskočí.')}`
}

/**
 * Whether the number asks for something the job cannot give.
 *
 * Only downwards now. A larger interval is honoured; a smaller one is not a disagreement worth
 * colouring red so much as a limit worth saying out loud, and the note says it.
 */
export function pollIntervalIgnored(form: HikvisionForm): boolean {
  const stored = Number(form.pollInterval.trim())
  return Number.isFinite(stored) && stored > 0 && stored < ACTUAL_POLL_SECONDS
}

// ─── FB-156: the webhook URL ─────────────────────────────────────────────────

/**
 * The URL the device should post to.
 *
 * Same origin as the app — dev proxies `/api` and production serves it alongside — and the **`/v1`** the
 * backend actually mounts. Legacy stripped the port off the manager's origin with a regular expression and
 * dropped the version, so the string it offered was for a host and a path that answer nothing.
 */
export function webhookUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/api/v1/hikvision/webhook`
}

/** Legacy's version, kept so a test can show the difference rather than describe it. */
export function legacyWebhookUrl(origin: string): string {
  return `${origin.replace(/:\d+$/, '')}/api/hikvision/webhook`
}

export const WEBHOOK_TOKEN_HEADER = 'X-Device-Token'

export function webhookSteps(): readonly string[] {
  return [
    tt('Prihláste sa do webového rozhrania zariadenia alebo do iVMS.'),
    'Configuration → Network → Advanced Settings → HTTP Listening',
    tt('Zapnite HTTP Listening a nastavte URL nižšie.'),
    `${tt('Ak máte nastavený Device Token, pošlite ho aj ako hlavičku')} ${WEBHOOK_TOKEN_HEADER}.`,
    tt('Uložte - zariadenie začne posielať udalosti samo.'),
  ]
}

// ─── Validation and the save ─────────────────────────────────────────────────

export type FieldProblem = 'ip' | 'port' | 'username' | 'poll' | null

/** An IPv4 address or a hostname; the device is on the restaurant's own network either way. */
function looksLikeHost(value: string): boolean {
  const host = value.trim()
  if (host === '') return false
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return host.split('.').every((part) => Number(part) <= 255)
  }
  return /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*$/.test(host)
}

/**
 * What is **wrong**, as opposed to what is merely **not filled in yet**.
 *
 * An empty box on a device nobody has configured is not an error to shout at somebody who has just opened the
 * page; it is the state the page is in. So `problemsOf` reports only values that are present and wrong, and
 * `missingOf` reports the empties. The save is gated on both; only the first gets red text.
 */
export function problemsOf(form: HikvisionForm): FieldProblem[] {
  const problems: FieldProblem[] = []
  if (form.ip.trim() !== '' && !looksLikeHost(form.ip)) problems.push('ip')
  const port = form.port.trim()
  if (port !== '') {
    const value = Number(port)
    if (!Number.isInteger(value) || value < 1 || value > 65535) problems.push('port')
  }
  const poll = form.pollInterval.trim()
  if (poll !== '') {
    const seconds = Number(poll)
    if (!Number.isInteger(seconds) || seconds < MIN_POLL_SECONDS || seconds > MAX_POLL_SECONDS) {
      problems.push('poll')
    }
  }
  return problems
}

/** The fields a save needs and does not have. Not an error — a state. */
export function missingOf(form: HikvisionForm): FieldProblem[] {
  const missing: FieldProblem[] = []
  if (form.ip.trim() === '') missing.push('ip')
  if (form.port.trim() === '') missing.push('port')
  if (form.username.trim() === '') missing.push('username')
  return missing
}

export function problemMessage(problem: FieldProblem): string {
  if (problem === 'ip') return tt('Zadajte IP adresu alebo názov zariadenia.')
  if (problem === 'port') return tt('Port je číslo od 1 do 65535.')
  if (problem === 'username') return tt('Bez menoslova sa k zariadeniu nedá prihlásiť.')
  if (problem === 'poll') {
    return `${tt('Interval je od')} ${MIN_POLL_SECONDS} ${tt('do')} ${MAX_POLL_SECONDS} ${tt('sekúnd.')}`
  }
  return ''
}

export function maySave(form: HikvisionForm, config: HikvisionConfig | null): boolean {
  if (problemsOf(form).length > 0 || missingOf(form).length > 0) return false
  // A first save needs a password: there is nothing stored to fall back on.
  return config !== null || form.password.trim() !== ''
}

/**
 * What to send.
 *
 * The password and the token go only when typed — the backend leaves the stored value alone for an empty
 * one, which is what makes the empty-field pattern safe.
 */
export function payloadOf(form: HikvisionForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ip: form.ip.trim(),
    port: Number(form.port.trim()) || DEFAULT_PORT,
    username: form.username.trim(),
    pollInterval: Number(form.pollInterval.trim()) || DEFAULT_POLL_SECONDS,
  }
  if (form.password.trim() !== '') payload.password = form.password
  if (form.deviceToken.trim() !== '') payload.deviceToken = form.deviceToken.trim()
  return payload
}

/** The test posts the typed connection details, so it can be run before a save. */
export function testPayloadOf(form: HikvisionForm): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ip: form.ip.trim(),
    port: Number(form.port.trim()) || DEFAULT_PORT,
    username: form.username.trim(),
  }
  if (form.password.trim() !== '') payload.password = form.password
  return payload
}

export function mayTest(form: HikvisionForm, config: HikvisionConfig | null): boolean {
  const blocking = [...problemsOf(form), ...missingOf(form)]
  if (blocking.includes('ip') || blocking.includes('port') || blocking.includes('username')) return false
  // A bad polling interval does not stop a connection test: it is not part of reaching the device.
  return config !== null || form.password.trim() !== ''
}
